import React, { useEffect, useRef, useState } from 'react'
import { useAuth } from 'react-oidc-context'

import { CognitoRefreshToken, CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js'
import { User } from 'oidc-client-ts'

import { COGNITO_CLIENT_ID, COGNITO_POOL_ID } from '../utils/Constants'

import { userManager } from './Auth'

export default function IdleTimeoutManager({ handleLogout }) {
  const auth = useAuth()
  const [showWarning, setShowWarning] = useState(false)
  const [countdown, setCountdown] = useState(120) // 2 minutes countdown
  const [errorMsg, setErrorMsg] = useState(null)

  const lastActivityRef = useRef(Date.now())
  const countdownIntervalRef = useRef(null)

  const isRefreshingRef = useRef(false)

  const refreshSession = async () => {
    if (isRefreshingRef.current || window.isRefreshingSession) {
      console.log('[IdleTimeoutManager] Session refresh already in progress. Skipping duplicate.')
      return
    }
    isRefreshingRef.current = true
    window.isRefreshingSession = true
    setErrorMsg(null)

    try {
      const currentUser = await userManager.getUser()
      if (!currentUser) {
        isRefreshingRef.current = false
        window.isRefreshingSession = false
        setErrorMsg('No current session found.')
        return
      }

      const refreshTokenValue = currentUser.refresh_token
      const username =
        currentUser.profile?.['cognito:username'] || currentUser.profile?.sub || currentUser.profile?.email

      if (!refreshTokenValue || !username) {
        isRefreshingRef.current = false
        window.isRefreshingSession = false
        setErrorMsg('Session tokens are stale or incomplete.')
        return
      }

      const userPool = new CognitoUserPool({
        UserPoolId: COGNITO_POOL_ID,
        ClientId: COGNITO_CLIENT_ID,
      })
      const cognitoUser = new CognitoUser({ Username: username, Pool: userPool })
      const refreshToken = new CognitoRefreshToken({ RefreshToken: refreshTokenValue })

      cognitoUser.refreshSession(refreshToken, async (err, session) => {
        isRefreshingRef.current = false
        window.isRefreshingSession = false
        if (err) {
          console.error('[IdleTimeoutManager] Proactive session refresh failed:', err)
          setErrorMsg(err.message || 'Cognito session refresh failed.')
          return
        }

        try {
          const updatedUser = new User({
            id_token: session.getIdToken().getJwtToken(),
            access_token: session.getAccessToken().getJwtToken(),
            refresh_token: session.getRefreshToken() ? session.getRefreshToken().getToken() : refreshTokenValue,
            token_type: currentUser.token_type || 'Bearer',
            scope: currentUser.scope || 'openid profile email',
            profile: session.getIdToken().payload,
            expires_at: session.getAccessToken().getExpiration(),
            state: currentUser.state,
          })

          // Persist the updated tokens back to oidc-client-ts storage
          await userManager.storeUser(updatedUser)

          // Clear error and close warning modal immediately upon successful persistence
          lastActivityRef.current = Date.now()
          setErrorMsg(null)
          setShowWarning(false)

          // Notify the user manager events so the React state updates
          if (userManager.events._userLoaded) {
            userManager.events._userLoaded.raise(updatedUser)
          }
        } catch (cbErr) {
          console.error('[IdleTimeoutManager] Exception in refresh callback:', cbErr)
          setErrorMsg(cbErr.message || 'Failed to update user session state.')
        }
      })
    } catch (err) {
      isRefreshingRef.current = false
      window.isRefreshingSession = false
      console.error('[IdleTimeoutManager] Error during proactive session refresh:', err)
      setErrorMsg(err.message || 'An unexpected error occurred during refresh.')
    }
  }

  // Listen to user activity to update lastActivityRef and trigger proactive renews
  useEffect(() => {
    if (!auth?.user) return

    const handleActivity = () => {
      lastActivityRef.current = Date.now()

      if (auth?.user?.expires_at) {
        const expiresAt = auth.user.expires_at * 1000
        const timeLeft = expiresAt - Date.now()
        const TEN_MINUTES = 10 * 60 * 1000

        // If the token is close to expiring (within 10 minutes) and we are active, refresh it
        if (timeLeft > 0 && timeLeft < TEN_MINUTES) {
          refreshSession()
        }
      }
    }

    const events = ['mousedown', 'click', 'keypress', 'scroll', 'touchstart']
    events.forEach(event => window.addEventListener(event, handleActivity))
    window.addEventListener('session-keep-alive', handleActivity)

    return () => {
      events.forEach(event => window.removeEventListener(event, handleActivity))
      window.removeEventListener('session-keep-alive', handleActivity)
    }
  }, [auth?.user])

  const handleTimeout = () => {
    if (auth?.removeUser) {
      auth.removeUser()
    }
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('oidc.user')) {
        localStorage.removeItem(key)
      }
    })
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search)
    window.location.replace(`/signin?returnTo=${returnTo}`)
  }

  // Check token lifetime and inactivity
  useEffect(() => {
    if (!auth?.user) {
      setShowWarning(false)
      return
    }

    const checkInterval = setInterval(() => {
      const expiresAt = auth.user.expires_at * 1000
      const timeLeft = expiresAt - Date.now()
      const timeSinceLastActivity = Date.now() - lastActivityRef.current

      // Show warning if token is expiring within 2 mins OR user has been inactive for 15 mins
      const INACTIVITY_LIMIT = 15 * 60 * 1000
      const WARNING_THRESHOLD = 2 * 60 * 1000

      const isTokenExpiring = timeLeft > 0 && timeLeft <= WARNING_THRESHOLD
      const isUserInactive = timeSinceLastActivity >= INACTIVITY_LIMIT

      if (isTokenExpiring || isUserInactive) {
        if (!showWarning) {
          // Start warning countdown
          setShowWarning(true)
          setErrorMsg(null)
          const secondsRemaining = Math.max(1, Math.min(120, Math.floor(timeLeft / 1000)))
          setCountdown(secondsRemaining)
        }
      } else {
        // If everything is fine, hide warning
        if (!errorMsg) {
          setShowWarning(false)
        }
      }

      // If token is fully expired or past its time, redirect/log out immediately,
      // but only if the warning modal is not currently open/active.
      if ((timeLeft <= 0 || auth.user.expired) && !showWarning) {
        clearInterval(checkInterval)
        handleTimeout()
      }
    }, 5000)

    return () => clearInterval(checkInterval)
  }, [auth?.user, showWarning, handleLogout])

  // Countdown timer logic when warning is shown
  useEffect(() => {
    if (showWarning) {
      countdownIntervalRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current)
            handleTimeout()
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } else {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current)
      }
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current)
      }
    }
  }, [showWarning, handleLogout])

  const handleStayLoggedIn = async () => {
    try {
      const currentUser = await userManager.getUser()
      if (currentUser) {
        const expiresAt = currentUser.expires_at * 1000
        const timeLeft = expiresAt - Date.now()
        const WARNING_THRESHOLD = 2 * 60 * 1000

        // If background process already extended the session, just close warning
        if (timeLeft > WARNING_THRESHOLD) {
          setErrorMsg(null)
          setShowWarning(false)
          return
        }
      }
    } catch (e) {
      console.warn('[IdleTimeoutManager] Pre-refresh check failed:', e)
    }
    refreshSession()
  }

  if (!showWarning) return null

  const formatTime = seconds => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="idle-timeout-backdrop">
      <div className="idle-timeout-modal">
        <h3 className="idle-timeout-title">Are you still there?</h3>
        <p className="idle-timeout-text">
          For your security, your session will automatically expire in{' '}
          <span className="idle-timeout-countdown">{formatTime(countdown)}</span> due to inactivity.
        </p>
        <p className="idle-timeout-text">
          If you need more time, just click the button below to stay logged in. Otherwise, you will be securely logged
          out.
        </p>
        {errorMsg && (
          <div className="alert alert-danger mb-3" style={{ fontSize: '0.9rem', textAlign: 'left' }}>
            <strong>Refresh Error:</strong> {errorMsg}
          </div>
        )}
        <div className="idle-timeout-actions">
          <button className="btn btn-secondary mr-2" onClick={handleLogout}>
            Log Out
          </button>
          <button className="btn btn-primary" onClick={handleStayLoggedIn}>
            Stay Logged In
          </button>
        </div>
      </div>
    </div>
  )
}
